import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import './App.css';
import { useAuthStore } from './store/authStore';
import MainLayout from './components/Layout/MainLayout';
import { appTheme } from './theme';

// Import pages
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterSupport from './pages/RegisterSupport';
import PasswordReset from './pages/PasswordReset';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import Dashboard from './pages/Dashboard';
import Menu from './pages/Menu';
import Users from './pages/Users';
import Profile from './pages/Profile';
import SupportAdmin from './pages/SupportAdmin';
import SupportTeam from './pages/SupportTeam';
import Support from './pages/Support';
import SalaryCalculation from './pages/SalaryCalculation';
import Orders from './pages/Orders';
import MyMenu from './pages/MyMenu';
import Settings from './pages/Settings';
import Limits from './pages/Limits';
import Placeholder from './pages/Placeholder';
import Reports from './pages/Reports';
import DeliveryAddresses from './pages/DeliveryAddresses';
import SupplierOrders from './pages/SupplierOrders';
import Documents from './pages/Documents';
import OrgLinkRequests from './pages/OrgLinkRequests';
import AdminApprovals from './pages/AdminApprovals';
import DocumentFlow from './pages/DocumentFlow';
import DocumentAcceptances from './pages/DocumentAcceptances';
import MyDeliveryTeam from './pages/MyDeliveryTeam';
import DeliveryDashboard from './pages/DeliveryDashboard';
import MySuppliers from './pages/MySuppliers';
import MyCustomers from './pages/MyCustomers';
import SupplierMenuPreview from './pages/SupplierMenuPreview';
import DishRatingsAnalytics from './pages/DishRatingsAnalytics';
import ThermalPrinting from './pages/ThermalPrinting';
import ForgotCredentials from './pages/ForgotCredentials';
import DocumentView from './pages/DocumentView';
import HomePage from './pages/HomePage';
import OrderIssues from './pages/OrderIssues';
import Guide from './pages/Guide';
import ChangelogPage from './pages/Changelog';
import SupportWidget from './components/SupportWidget';
import YandexMetrika from './components/YandexMetrika';
import MyOrderHistory from './pages/MyOrderHistory';

function App() {
  const { initialize } = useAuthStore();

  useEffect(() => {
    // Force HTTPS on production domains (mobile browsers sometimes open http:// by default)
    const host = window.location.hostname;
    if (
      window.location.protocol === 'http:' &&
      (host === 'luemen.ru' || host.endsWith('.luemen.ru'))
    ) {
      const next = `https://${host}${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(next);
      return;
    }
    initialize();
  }, [initialize]);

  return (
    <ConfigProvider locale={ruRU} theme={appTheme}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
          <Routes>
            {/* Public routes - без MainLayout */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-support" element={<RegisterSupport />} />
            <Route path="/password-reset" element={<PasswordReset />} />
            <Route path="/forgot-credentials" element={<ForgotCredentials />} />
            
            {/* Document routes - доступны без авторизации */}
            <Route path="/document/:documentKey" element={<DocumentView />} />
            <Route path="/terms" element={<DocumentView />} />
            <Route path="/privacy" element={<DocumentView />} />
            
            {/* Root route - показываем HomePage для неавторизованных, редирект для авторизованных */}
            <Route path="/" element={<HomePage />} />

            {/* Public guide route (без авторизации и без MainLayout) */}
            <Route path="/platform-guide" element={<Guide />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            
            {/* Dashboard route - редирект в зависимости от роли для авторизованных */}
            <Route path="/app" element={<MainLayout><RoleBasedRedirect /></MainLayout>} />
            
            {/* Protected routes - с MainLayout */}
            <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />
            <Route path="/menu" element={<MainLayout><Menu /></MainLayout>} />
            <Route path="/my-menu" element={<MainLayout><MyMenu /></MainLayout>} />
            <Route path="/users" element={<MainLayout><Users /></MainLayout>} />
            <Route path="/profile" element={<MainLayout><Profile /></MainLayout>} />
            <Route path="/support" element={<MainLayout><Support /></MainLayout>} />
            <Route path="/support-team" element={<MainLayout><SupportTeam /></MainLayout>} />
            <Route path="/support-admin" element={<MainLayout><SupportAdmin /></MainLayout>} />
            <Route path="/salary-calculation" element={<MainLayout><SalaryCalculation /></MainLayout>} />
            <Route path="/orders" element={<MainLayout><Orders /></MainLayout>} />
            <Route path="/settings" element={<MainLayout><Settings /></MainLayout>} />
            
            {/* Admin routes */}
            <Route path="/admin/approvals" element={<MainLayout><AdminApprovals /></MainLayout>} />
            <Route path="/admin/documents" element={<MainLayout><Documents /></MainLayout>} />
            <Route path="/admin/document-acceptances" element={<MainLayout><DocumentAcceptances /></MainLayout>} />
            
            {/* Customer routes */}
            <Route path="/org/user-approvals" element={<MainLayout><Placeholder title="Заявки сотрудников" /></MainLayout>} />
            <Route path="/limits" element={<MainLayout><Limits /></MainLayout>} />
            <Route path="/my-suppliers" element={<MainLayout><MySuppliers /></MainLayout>} />
            <Route path="/supplier-menu/:supplierId" element={<MainLayout><SupplierMenuPreview /></MainLayout>} />
            <Route path="/delivery-addresses" element={<MainLayout><DeliveryAddresses /></MainLayout>} />
            <Route path="/my-delivery-team" element={<MainLayout><MyDeliveryTeam /></MainLayout>} />
            
            {/* Supplier routes */}
            <Route path="/my-customers" element={<MainLayout><MyCustomers /></MainLayout>} />
            <Route path="/org/link-requests" element={<MainLayout><OrgLinkRequests /></MainLayout>} />
            <Route path="/supplier-orders" element={<MainLayout><SupplierOrders /></MainLayout>} />
            <Route path="/dish-ratings" element={<MainLayout><DishRatingsAnalytics /></MainLayout>} />
            <Route path="/thermal-printing" element={<MainLayout><ThermalPrinting /></MainLayout>} />
            
            {/* Employee routes */}
            <Route path="/order-issues" element={<MainLayout><OrderIssues /></MainLayout>} />
            <Route path="/my-order-history" element={<MainLayout><MyOrderHistory /></MainLayout>} />
            
            {/* Delivery routes */}
            <Route path="/delivery-dashboard" element={<MainLayout><DeliveryDashboard /></MainLayout>} />
            
            {/* Common routes */}
            <Route path="/documents" element={<MainLayout><DocumentFlow /></MainLayout>} />
            <Route path="/guide" element={<MainLayout><Guide /></MainLayout>} />
            <Route path="/app/changelog" element={<MainLayout><ChangelogPage /></MainLayout>} />
            <Route path="/reports" element={<MainLayout><Reports /></MainLayout>} />
            
            {/* Deprecated duplicate routes */}
            <Route path="/admin/policies" element={<MainLayout><Documents /></MainLayout>} />
          </Routes>
          <SupportWidget />
          <YandexMetrika />
        </div>
      </Router>
    </ConfigProvider>
  );
}

export default App;